import CustomHTMLElement from '@onpace/onspace-core/components/html_element'

/// Displays a notification overlay.
///
/// This passes the arguments directly to the constructor of NotificationElement.
export function showNotification(options) {
  const notification = new NotificationElement(options)
  notification.show()
}

window.onspaceShowNotification = showNotification

////////// Container

const notificationsContainer = document.createElement('div')
notificationsContainer.classList.add('onspace-notifications')
document.addEventListener('turbo:load', (_event) => {
  document.body.appendChild(notificationsContainer)
})

////////// Element

/// An interactive overlay with a small amount of information.
class NotificationElement extends CustomHTMLElement {
  /// Sets up the notification element.
  ///
  /// This accepts an object with the following attributes:
  /// [title]
  ///   Title text to display on the notification.
  /// [body]
  ///   Text to display on the notification.
  /// [color]
  ///   The color of the notification.
  /// [prompt]
  ///   Footer text to display indicating a user prompt.
  /// [onPress]
  ///   A callback which is run when the notification is clicked or tapped.
  /// [timeout]
  ///   The duration in milliseconds to display the notification until it is automatically removed. This is +5000+ by
  ///   default, set this to +false+ to leave it permanently.
  runConstructor(options = {}) {
    super.runConstructor()

    this.setupContent(options.title, options.body, options.prompt, options.color)

    this.onPress = options.onPress
    this.addEventListener('click', this.pressed.bind(this))

    if (typeof options.timeout === 'number') {
      this.setupTimeout(options.timeout)
    } else if (typeof options.timeout === 'undefined') {
      this.setupTimeout(5000)
    }
  }

  /// Sets up the content for display.
  setupContent(title, body, prompt, color) {
    this.contentElement = document.createElement('div')
    this.contentElement.classList.add('onspace-notification__content')
    this.appendChild(this.contentElement)

    const closeButton = document.createElement('a')
    closeButton.classList.add('onspace-notification__close')
    closeButton.addEventListener('click', this.closePressed.bind(this))
    this.contentElement.appendChild(closeButton)

    const closeIcon = SVGElement.createOnspaceSpritemapSvg('onspace/icon_cross')
    closeButton.appendChild(closeIcon)

    if (typeof title === 'string' && title.length > 0) {
      const titleElement = document.createElement('div')
      titleElement.classList.add('onspace-notification__title')
      titleElement.innerText = title
      this.contentElement.appendChild(titleElement)
    }

    if (typeof body === 'string' && body.length > 0) {
      const bodyElement = document.createElement('div')
      bodyElement.classList.add('onspace-notification__body')
      bodyElement.innerText = body
      this.contentElement.appendChild(bodyElement)
    }

    if (typeof prompt === 'string' && prompt.length > 0) {
      const promptElement = document.createElement('div')
      promptElement.classList.add('onspace-notification__prompt')
      promptElement.innerText = prompt
      this.contentElement.appendChild(promptElement)
    }

    if (typeof color === 'string' && color.length > 0) {
      this.contentElement.classList.add(`onspace-notification--color-${color}`)
    }
  }

  /// Sets a timer to automatically clear the notification after the given duration.
  setupTimeout(duration) {
    setTimeout(() => this.clear(), duration)
  }

  //////////

  /// Animates the display of the notification.
  show() {
    notificationsContainer.appendChild(this)

    const animationEnded = (_event) => {
      this.removeEventListener('animationend', animationEnded)
      this.style.height = ''
      this.classList.remove('onspace-notification--show')
    }
    this.addEventListener('animationend', animationEnded)

    const rect = this.contentElement.getBoundingClientRect()
    this.style.height = `${rect.height}px`
    this.classList.add('onspace-notification--show')
  }

  /// Animates the removal of the notification.
  clear() {
    if (!this.parentElement || this.classList.contains('onspace-notification--hide')) { return }

    const animationEnded = (_event) => {
      this.removeEventListener('animationend', animationEnded)
      this.style.height = ''
      this.classList.remove('onspace-notification--hide')

      this.remove()
    }
    this.addEventListener('animationend', animationEnded)

    const rect = this.contentElement.getBoundingClientRect()
    this.style.height = `${rect.height}px`
    this.classList.add('onspace-notification--hide')
  }

  /// Responds to presses on the close element.
  closePressed(event) {
    event.preventDefault()
    event.stopPropagation()

    this.clear()
  }

  /// Responds to presses on the element.
  ///
  /// This will run the +onPress+ callback if available, and clears the notification.
  pressed() {
    if (typeof this.onPress === 'function') { this.onPress() }
    this.clear()
  }
}

window.customElements.define('onspace-notification', NotificationElement)
